import React, { useState } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { FacilitiesData } from '../FacilitiesModal';
import './FacilitiesEntry.css';

interface Bedroom {
  id: string;
  location: 'upstairs' | 'downstairs';
  doubleBeds: number;
  singleBeds: number;
  bunksAndCots: number;
  notes: string;
}

interface LoadedUnit {
  id: string;
  placeCode: string;
  placeName: string;
  placeType: string;
  facilitiesData?: FacilitiesData;
}

const defaultFacilitiesData: FacilitiesData = {
  sleepCapacity: 0,
  bedrooms: [],
  livingRoom: {
    hasCouch: false,
    hasChairs: false,
    chairCount: 0,
    hasExtraSleeping: false,
    notes: ''
  },
  kitchen: {
    hasFridge: false,
    fridgeSize: 'standard',
    hasTable: false,
    chairCount: 0,
    hasMicrowave: false,
    hasHotplate: false,
    hasDishes: false,
    hasPotsAndPans: false,
    hasDishPan: false,
    hasToaster: false,
    hasCoffeeMaker: false,
    hasKettle: false,
    hasSink: false,
    otherItems: ''
  },
  bathroom: {
    hasBathroom: false,
    hasSink: false,
    hasHotWater: false,
    hasToilet: false,
    hasShower: false
  },
  access: {
    stepsToEnter: 0,
    stepsInside: 0,
    hasLoft: false
  },
  additionalNotes: '',
  needsFixing: ''
};

const FacilitiesEntry: React.FC = () => {
  const [placeCode, setPlaceCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentUnit, setCurrentUnit] = useState<LoadedUnit | null>(null);
  const [formData, setFormData] = useState<FacilitiesData>(defaultFacilitiesData);
  const [bedrooms, setBedrooms] = useState<Bedroom[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);

  const loadUnit = async () => {
    if (!placeCode.trim()) {
      setError('Please enter a place code');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const inventoryRef = doc(db, 'inventory', 'accommodations');
      const docSnap = await getDoc(inventoryRef);

      if (!docSnap.exists()) {
        setError('Inventory not found');
        setLoading(false);
        return;
      }

      const data = docSnap.data();
      const types = ['Cottage', 'Apartment', 'dormA', 'dormB', 'Fireside', 'LowerCourt', 'UpperCourt', 'privateCottage'];

      for (const type of types) {
        if (Array.isArray(data[type])) {
          const found = data[type].find((acc: any) => 
            acc.placeCode.toUpperCase() === placeCode.trim().toUpperCase()
          );
          if (found) {
            setCurrentUnit({
              id: found.id,
              placeCode: found.placeCode,
              placeName: found.placeName || found.placeCode,
              placeType: type,
              facilitiesData: found.facilitiesData
            });
            
            const fd = found.facilitiesData || defaultFacilitiesData;
            setFormData(fd);
            setBedrooms(fd.bedrooms || []);
            setIsExpanded(true);
            setLoading(false);
            return;
          }
        }
      }

      setError(`Unit "${placeCode}" not found in inventory`);
    } catch (err: any) {
      setError('Error loading unit: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!currentUnit) return;

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const facilitiesData: FacilitiesData = {
        ...formData,
        bedrooms
      };

      const inventoryRef = doc(db, 'inventory', 'accommodations');
      const docSnap = await getDoc(inventoryRef);
      const data = docSnap.data();

      if (!data) {
        setError('Inventory data not found');
        setSaving(false);
        return;
      }

      const updatedAccommodations = data[currentUnit.placeType].map((acc: any) =>
        acc.id === currentUnit.id
          ? { ...acc, facilitiesData }
          : acc
      );

      await setDoc(inventoryRef, {
        ...data,
        [currentUnit.placeType]: updatedAccommodations
      });

      setSuccess(`✓ Saved facilities data for ${currentUnit.placeCode}`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError('Error saving: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleClear = () => {
    setCurrentUnit(null);
    setPlaceCode('');
    setFormData(defaultFacilitiesData);
    setBedrooms([]);
    setError('');
    setSuccess('');
    setIsExpanded(false);
  };

  const addBedroom = () => {
    if (bedrooms.length >= 5) return;
    setBedrooms([...bedrooms, {
      id: Date.now().toString(),
      location: 'upstairs',
      doubleBeds: 0,
      singleBeds: 0,
      bunksAndCots: 0,
      notes: ''
    }]);
  };

  const removeBedroom = (id: string) => {
    setBedrooms(bedrooms.filter(b => b.id !== id));
  };

  const updateBedroom = (id: string, field: keyof Bedroom, value: any) => {
    setBedrooms(bedrooms.map(b => 
      b.id === id ? { ...b, [field]: value } : b
    ));
  };

  return (
    <section className="facilities-entry-section">
      <div className="action-section">
        <div className="action-header">
          <h3>Facilities Data Entry</h3>
          <p>Quickly enter accommodation details from paper forms.</p>
        </div>

        {/* Unit Selector */}
        <div className="fe-unit-selector">
          <input
            type="text"
            value={placeCode}
            onChange={(e) => setPlaceCode(e.target.value.toUpperCase())}
            onKeyPress={(e) => e.key === 'Enter' && loadUnit()}
            placeholder="Place Code (e.g., 102)"
            disabled={loading}
          />
          <button onClick={loadUnit} disabled={loading} className="action-button">
            {loading ? 'Loading...' : 'Load Unit'}
          </button>
          {currentUnit && (
            <span className="fe-unit-loaded">
              ✓ {currentUnit.placeName} ({currentUnit.placeType})
            </span>
          )}
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        {/* Form - only show when unit is loaded */}
        {isExpanded && currentUnit && (
          <div className="fe-form">
            
            {/* Sleep Capacity */}
            <div className="fe-section">
              <h4>🛏️ Sleeping Capacity</h4>
              <div className="fe-field-row">
                <label>Total sleep capacity:</label>
                <input
                  type="number"
                  min="0"
                  value={formData.sleepCapacity}
                  onChange={(e) => setFormData({ ...formData, sleepCapacity: parseInt(e.target.value) || 0 })}
                />
                <span>people</span>
              </div>
            </div>

            {/* Bedrooms */}
            <div className="fe-section">
              <h4>🚪 Bedrooms</h4>
              {bedrooms.map((br, idx) => (
                <div key={br.id} className="fe-bedroom-card">
                  <div className="fe-bedroom-header">
                    <strong>Bedroom {idx + 1}</strong>
                    <button className="fe-remove-btn" onClick={() => removeBedroom(br.id)}>×</button>
                  </div>
                  <div className="fe-bedroom-fields">
                    <label>
                      Location:
                      <select
                        value={br.location}
                        onChange={(e) => updateBedroom(br.id, 'location', e.target.value)}
                      >
                        <option value="upstairs">Upstairs</option>
                        <option value="downstairs">Downstairs</option>
                      </select>
                    </label>
                    <label>
                      Double:
                      <input type="number" min="0" value={br.doubleBeds} 
                        onChange={(e) => updateBedroom(br.id, 'doubleBeds', parseInt(e.target.value) || 0)} />
                    </label>
                    <label>
                      Single:
                      <input type="number" min="0" value={br.singleBeds}
                        onChange={(e) => updateBedroom(br.id, 'singleBeds', parseInt(e.target.value) || 0)} />
                    </label>
                    <label>
                      Bunks/Cots:
                      <input type="number" min="0" value={br.bunksAndCots}
                        onChange={(e) => updateBedroom(br.id, 'bunksAndCots', parseInt(e.target.value) || 0)} />
                    </label>
                    <label className="fe-notes-label">
                      Notes:
                      <input type="text" value={br.notes}
                        onChange={(e) => updateBedroom(br.id, 'notes', e.target.value)}
                        placeholder="e.g., Queen bed" />
                    </label>
                  </div>
                </div>
              ))}
              {bedrooms.length < 5 && (
                <button className="fe-add-btn" onClick={addBedroom}>+ Add Bedroom</button>
              )}
            </div>

            {/* Access */}
            <div className="fe-section">
              <h4>♿ Access</h4>
              <div className="fe-field-row">
                <label>Steps to enter:</label>
                <input type="number" min="0" value={formData.access.stepsToEnter}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    access: { ...formData.access, stepsToEnter: parseInt(e.target.value) || 0 }
                  })} />
              </div>
              <div className="fe-field-row">
                <label>Steps inside (if 2 floors):</label>
                <input type="number" min="0" value={formData.access.stepsInside}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    access: { ...formData.access, stepsInside: parseInt(e.target.value) || 0 }
                  })} />
              </div>
              <div className="fe-checkbox-row">
                <input type="checkbox" id="fe-hasLoft" checked={formData.access.hasLoft}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    access: { ...formData.access, hasLoft: e.target.checked }
                  })} />
                <label htmlFor="fe-hasLoft">Has loft (ladder access)</label>
              </div>
            </div>

            {/* Living Room */}
            <div className="fe-section">
              <h4>🛋️ Living Room</h4>
              <div className="fe-checkbox-grid">
                <div className="fe-checkbox-row">
                  <input type="checkbox" id="fe-hasCouch" checked={formData.livingRoom.hasCouch}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      livingRoom: { ...formData.livingRoom, hasCouch: e.target.checked }
                    })} />
                  <label htmlFor="fe-hasCouch">Couch</label>
                </div>
                <div className="fe-checkbox-row">
                  <input type="checkbox" id="fe-hasExtraSleeping" checked={formData.livingRoom.hasExtraSleeping}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      livingRoom: { ...formData.livingRoom, hasExtraSleeping: e.target.checked }
                    })} />
                  <label htmlFor="fe-hasExtraSleeping">Extra sleeping space</label>
                </div>
              </div>
              <div className="fe-field-row">
                <input type="checkbox" checked={formData.livingRoom.hasChairs}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    livingRoom: { ...formData.livingRoom, hasChairs: e.target.checked }
                  })} />
                <label>Chairs (other than kitchen)</label>
                {formData.livingRoom.hasChairs && (
                  <>
                    <label>How many:</label>
                    <input type="number" min="0" value={formData.livingRoom.chairCount} style={{ width: 60 }}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        livingRoom: { ...formData.livingRoom, chairCount: parseInt(e.target.value) || 0 }
                      })} />
                  </>
                )}
              </div>
            </div>

            {/* Kitchen */}
            <div className="fe-section">
              <h4>🍳 Kitchen</h4>
              <div className="fe-field-row">
                <input type="checkbox" checked={formData.kitchen.hasFridge}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    kitchen: { ...formData.kitchen, hasFridge: e.target.checked }
                  })} />
                <label>Fridge</label>
                {formData.kitchen.hasFridge && (
                  <select value={formData.kitchen.fridgeSize}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      kitchen: { ...formData.kitchen, fridgeSize: e.target.value as any }
                    })}>
                    <option value="standard">Standard</option>
                    <option value="apartment">Apartment</option>
                    <option value="dorm">Dorm</option>
                  </select>
                )}
              </div>
              <div className="fe-field-row">
                <input type="checkbox" checked={formData.kitchen.hasTable}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    kitchen: { ...formData.kitchen, hasTable: e.target.checked }
                  })} />
                <label>Table</label>
                {formData.kitchen.hasTable && (
                  <>
                    <label>Chairs:</label>
                    <input type="number" min="0" value={formData.kitchen.chairCount} style={{ width: 60 }}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        kitchen: { ...formData.kitchen, chairCount: parseInt(e.target.value) || 0 }
                      })} />
                  </>
                )}
              </div>
              <div className="fe-checkbox-grid">
                {[
                  { key: 'hasMicrowave', label: 'Microwave' },
                  { key: 'hasHotplate', label: 'Hotplate' },
                  { key: 'hasDishes', label: 'Dishes' },
                  { key: 'hasPotsAndPans', label: 'Pots & Pans' },
                  { key: 'hasDishPan', label: 'Dish Pan' },
                  { key: 'hasToaster', label: 'Toaster' },
                  { key: 'hasCoffeeMaker', label: 'Coffee Maker' },
                  { key: 'hasKettle', label: 'Kettle' },
                  { key: 'hasSink', label: 'Sink' }
                ].map(item => (
                  <div key={item.key} className="fe-checkbox-row">
                    <input type="checkbox" 
                      checked={(formData.kitchen as any)[item.key]}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        kitchen: { ...formData.kitchen, [item.key]: e.target.checked }
                      })} />
                    <label>{item.label}</label>
                  </div>
                ))}
              </div>
              <div className="fe-field-row">
                <label>Other items:</label>
                <input type="text" value={formData.kitchen.otherItems} style={{ flex: 1 }}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    kitchen: { ...formData.kitchen, otherItems: e.target.value }
                  })}
                  placeholder="Other kitchen items..." />
              </div>
            </div>

            {/* Bathroom */}
            <div className="fe-section">
              <h4>🚿 Bathroom</h4>
              <div className="fe-checkbox-grid">
                {[
                  { key: 'hasBathroom', label: 'Has Bathroom' },
                  { key: 'hasSink', label: 'Sink w/ running water' },
                  { key: 'hasHotWater', label: 'Hot water' },
                  { key: 'hasToilet', label: 'Toilet' },
                  { key: 'hasShower', label: 'Shower' }
                ].map(item => (
                  <div key={item.key} className="fe-checkbox-row">
                    <input type="checkbox" 
                      checked={(formData.bathroom as any)[item.key]}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        bathroom: { ...formData.bathroom, [item.key]: e.target.checked }
                      })} />
                    <label>{item.label}</label>
                  </div>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div className="fe-section">
              <h4>📝 Notes</h4>
              <div className="fe-field-row">
                <label>What should people know:</label>
                <textarea value={formData.additionalNotes}
                  onChange={(e) => setFormData({ ...formData, additionalNotes: e.target.value })}
                  placeholder="What else should people know about this place?" />
              </div>
              <div className="fe-field-row">
                <label>Needs fixing:</label>
                <textarea value={formData.needsFixing}
                  onChange={(e) => setFormData({ ...formData, needsFixing: e.target.value })}
                  placeholder="Is there anything that might need fixing?" />
              </div>
            </div>

            {/* Actions */}
            <div className="fe-actions">
              <button className="fe-save-btn" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save to Database'}
              </button>
              <button className="fe-clear-btn" onClick={handleClear}>
                Clear for Next Unit
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default FacilitiesEntry;




